/**
 * MFA Setup Component
 *
 * Allows users to enable TOTP-based MFA using authenticator apps.
 * Displays QR code for easy setup with Google Authenticator, Authy, etc.
 *
 * Flow:
 * 1. User clicks "Enable MFA"
 * 2. Backend generates TOTP secret and QR code
 * 3. User scans QR code with authenticator app
 * 4. User enters 6-digit code to verify
 * 5. MFA is enabled
 *
 * Part of Phase 3: TOTP MFA for Staff Accounts
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useNotify } from 'react-admin';
import config from '../config';
import Logger from '../utils/Logger';
import { getValidToken } from '../services/tokenRefreshService';

interface MfaSetupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SetupData {
  secretCode: string;
  qrCodeUrl: string;
}

const steps = ['Scan QR Code', 'Verify Code', 'Complete'];

export const MfaSetup = ({ open, onClose, onSuccess }: MfaSetupProps) => {
  const notify = useNotify();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');

  // Step 1: Get QR code from backend
  const handleStartSetup = async () => {
    setLoading(true);
    setError('');

    try {
      const token = await getValidToken();
      const response = await fetch(`${config.API_URL}/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to setup MFA');
      }

      const data = await response.json();
      setSetupData(data);
      setActiveStep(1); // Move to verification step
    } catch (err) {
      Logger.error('[MFA Setup] Error:', err);
      setError('Failed to initialize MFA setup. Please try again.');
      notify('Failed to setup MFA', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify 6-digit code
  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await getValidToken();
      const response = await fetch(`${config.API_URL}/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid verification code');
      }

      setActiveStep(2); // Move to completion step
      notify('MFA enabled successfully', { type: 'success' });

      // Auto-close and refresh after 2 seconds
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } catch (err: any) {
      Logger.error('[MFA Verify] Error:', err);
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSetupData(null);
    setVerificationCode('');
    setError('');
    onClose();
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step 0: Introduction */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Two-factor authentication adds an extra layer of security to your account.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                You'll need an authenticator app like:
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2">Google Authenticator</Typography>
                </li>
                <li>
                  <Typography variant="body2">Microsoft Authenticator</Typography>
                </li>
                <li>
                  <Typography variant="body2">Authy</Typography>
                </li>
              </ul>
            </Box>
          )}

          {/* Step 1: QR Code */}
          {activeStep === 1 && setupData && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom>
                Scan this QR code with your authenticator app
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={setupData.qrCodeUrl}
                  alt="MFA QR Code"
                  style={{ maxWidth: '250px', border: '1px solid #ccc', padding: '10px' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Or enter this code manually: <strong>{setupData.secretCode}</strong>
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Enter the 6-digit code from your app:
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      fontSize: '24px',
                      textAlign: 'center',
                      letterSpacing: '8px',
                    },
                  }}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          )}

          {/* Step 2: Success */}
          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="success">
                <Typography variant="h6" gutterBottom>
                  MFA Enabled Successfully!
                </Typography>
                <Typography variant="body2">
                  Your account is now protected with two-factor authentication.
                  You'll need to enter a code from your authenticator app when signing in.
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleStartSetup}
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Setting up...' : 'Start Setup'}
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleVerifyCode}
              variant="contained"
              disabled={loading || verificationCode.length !== 6}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MfaSetup;
